<script setup>
import { nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['close']);
const router = useRouter();
const { t } = useI18n();

const isConnected = ref(false);
const isLoading = ref(false);
const loadError = ref('');
const integrationLinkRef = ref(null);
const retryButtonRef = ref(null);

const accountId = router.currentRoute.value.params.accountId;
const settingsUrl = frontendURL(
  `accounts/${accountId}/settings/integrations/openai`
);

const fetchIntegration = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  loadError.value = '';
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId}/integrations/apps`
    );
    const openaiIntegration = response.data?.payload?.find(
      integration => integration.id === 'openai'
    );
    isConnected.value = Boolean(openaiIntegration?.hooks?.length);
  } catch {
    isConnected.value = false;
    loadError.value = t('PIPELINE_MODALS.AI.LOAD_ERROR');
  } finally {
    isLoading.value = false;
    await nextTick();
    const activeElement = document.activeElement;
    const shouldMoveFocus =
      !activeElement ||
      activeElement === document.body ||
      activeElement.classList?.contains('modal-container');
    if (shouldMoveFocus) {
      if (loadError.value) retryButtonRef.value?.$el?.focus();
      else integrationLinkRef.value?.focus();
    }
  }
};

const handleClose = () => emit('close');

onMounted(fetchIntegration);
</script>

<template>
  <Modal
    show
    initial-focus-selector="#ai-config-integration-link"
    @close="handleClose"
  >
    <section
      class="flex max-h-[min(90vh,48rem)] flex-col overflow-hidden"
      :aria-busy="isLoading"
      data-modal="ai-config"
    >
      <header class="border-b border-n-weak px-4 py-4 sm:px-6">
        <div class="flex min-w-0 flex-wrap items-center gap-3 pr-10">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.AI_CONFIG.TITLE') }}
          </h2>
          <span
            v-if="!isLoading && !loadError"
            role="status"
            aria-live="polite"
            class="rounded-full bg-n-alpha-2 px-2 py-1 text-label-small font-medium text-n-slate-11"
          >
            {{
              isConnected
                ? t('PIPELINE.AI_CONFIG.STATUS.CONNECTED')
                : t('PIPELINE.AI_CONFIG.STATUS.DISCONNECTED')
            }}
          </span>
        </div>
      </header>

      <div class="overflow-y-auto px-4 py-5 sm:px-6">
        <div
          v-if="isLoading"
          class="flex min-h-40 flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite"
        >
          <Spinner class="!size-6" />
          <span class="text-sm text-n-slate-11">
            {{ t('PIPELINE_MODALS.AI.LOADING') }}
          </span>
        </div>

        <div
          v-else-if="loadError"
          class="flex min-h-40 flex-col items-center justify-center gap-4 text-center"
          role="alert"
        >
          <p class="text-sm text-n-ruby-11">{{ loadError }}</p>
          <Button
            ref="retryButtonRef"
            variant="faded"
            color="slate"
            size="lg"
            icon="i-lucide-rotate-ccw"
            :label="t('PIPELINE_MODALS.RETRY')"
            @click="fetchIntegration"
          />
        </div>

        <div v-else class="space-y-4">
          <p id="ai-config-description" class="text-body-main text-n-slate-11">
            {{ t('PIPELINE.AI_CONFIG.DESCRIPTION') }}
          </p>
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            role="status"
            aria-live="polite"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{
                isConnected
                  ? t('PIPELINE.AI_CONFIG.CONNECTED_TITLE')
                  : t('PIPELINE.AI_CONFIG.DISCONNECTED_TITLE')
              }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                isConnected
                  ? t('PIPELINE.AI_CONFIG.CONNECTED_DESCRIPTION')
                  : t('PIPELINE.AI_CONFIG.DISCONNECTED_DESCRIPTION')
              }}
            </p>
          </div>
        </div>
      </div>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak px-4 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <Button
          variant="ghost"
          color="slate"
          size="lg"
          :label="t('PIPELINE.AI_CONFIG.CLOSE_LABEL')"
          class="w-full sm:w-auto"
          @click="handleClose"
        />
        <a
          v-if="!isLoading && !loadError"
          id="ai-config-integration-link"
          ref="integrationLinkRef"
          data-ai-integration-cta
          :href="settingsUrl"
          aria-describedby="ai-config-description"
          class="inline-flex min-h-12 w-full items-center justify-center rounded-lg bg-n-brand px-5 text-base font-medium text-white outline-none transition-colors hover:brightness-110 focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 sm:w-auto"
        >
          {{
            isConnected
              ? t('PIPELINE.AI_CONFIG.MANAGE_INTEGRATION')
              : t('PIPELINE.AI_CONFIG.CONFIGURE_INTEGRATION')
          }}
        </a>
      </footer>
    </section>
  </Modal>
</template>
